import { Label, _decorator } from 'cc';
import { DEV, EDITOR } from 'cc/env';
import I18n from '../../../I18n/I18n';
import GameDependency from '../../../System/GameDependency';
import NumberFormatter from './NumberFormatter';
import { fillTextArgument } from '../../Utils/Utility';

enum EventType {
    TEXT_DISPLAY_CHANGED = 'TEXT_DISPLAY_CHANGED',
}

const { ccclass, property, requireComponent, executeInEditMode } = _decorator;

@ccclass('MultiNumberFormatter')
@requireComponent(Label)
@executeInEditMode
export default class MultiNumberFormatter extends GameDependency {
    @property
    private _textTemplate: string = '';

    @property
    private _localizedTextTemplate: string = '';

    @property([NumberFormatter])
    private _formatters: NumberFormatter[] = [];

    private _label: Label = null;
    private _disabled: boolean = false;

    @property([NumberFormatter])
    public set formatters(value: NumberFormatter[]) {
        if (this._formatters.length) {
            // unregister event listener
            this._formatters.forEach((formatter) => this._unregisterFormatterEvents(formatter));
        }
        this._formatters = value;
        if (this._formatters.length) {
            this._formatters.forEach((formatter) => {
                // disable label of formatter, because it will not be displayed, we just use its formatted string
                if (formatter) {
                    formatter.label.enabled = false;
                }
                this._registerFormatterEvents(formatter);
            });
        }
        this.updateTextDisplay();
    }
    public get formatters() {
        return this._formatters;
    }

    @property({
        tooltip: DEV && '可指定任意文字格式，並以 {0, 1, 2...} 指定 Value 位置，此字串不為空，且 localizedTextTemplate 未套用時會套用',
        multiline: true,
    })
    public set textTemplate(value: string) {
        if (this._textTemplate === value) {
            return;
        }
        this._textTemplate = value;
        this.updateTextDisplay();
    }
    public get textTemplate() {
        return this._textTemplate;
    }

    @property({ tooltip: DEV && '可指定任意多語系格式，並以 {0, 1, 2...} 指定 Value 位置，此字串不為空時會套用 (編輯器中無法生效)', multiline: true })
    public set localizedTextTemplate(value: string) {
        if (this._localizedTextTemplate === value) {
            return;
        }
        this._localizedTextTemplate = value;
        this.updateTextDisplay();
    }
    public get localizedTextTemplate() {
        return this._localizedTextTemplate;
    }

    public set values(values: number[]) {
        values.forEach((value) => {
            if (typeof value !== 'number' || Number.isNaN(value)) {
                throw new Error(`invalid value: ${value}`);
            }
        });
        this._formatters.forEach((formatter, index) => {
            if (formatter) {
                formatter.value = values[index] ?? 0;
            }
        });
    }
    public get values() {
        return this._formatters.map((formatter) => formatter?.value ?? 0);
    }

    /**
     * disable text updating, which means the text won't be updated by setting value, but value will still be stored.
     */
    public get disabled() {
        return this._disabled;
    }
    public set disabled(value: boolean) {
        if (this._disabled === value) {
            return;
        }
        this._disabled = value;
        this.updateTextDisplay();
    }

    private get label() {
        if (!this._label) {
            this._label = this.getComponent(Label);
        }
        return this._label;
    }

    public onLoad() {
        if (!EDITOR) {
            this.game.i18n.on(
                I18n.EVENT_TYPE.LanguageChanged,
                () => {
                    if (this._localizedTextTemplate) {
                        this.updateTextDisplay();
                    }
                },
                this,
            );
        }
        this.formatters.forEach((formatter) => this._registerFormatterEvents(formatter));
    }

    public start() {
        this.updateTextDisplay();
    }

    public onDestroy() {
        this.formatters.forEach((formatter) => this._unregisterFormatterEvents(formatter));
    }

    public updateTextDisplay() {
        if (this.disabled) {
            return;
        }
        const textTemplate = this._localizedTextTemplate && !EDITOR ? this.game.i18n.getMessage(this._localizedTextTemplate) : this._textTemplate;
        this.label.string = fillTextArgument(
            textTemplate,
            this.formatters.map((formatter) => formatter?.label.string ?? ''),
        );
        this.emit(EventType.TEXT_DISPLAY_CHANGED, this.label.string);
    }

    private _registerFormatterEvents(formatter: NumberFormatter) {
        if (!formatter) {
            return;
        }
        formatter.on(NumberFormatter.EVENT_TYPE.TEXT_DISPLAY_UPDATED, this.updateTextDisplay, this);
    }

    private _unregisterFormatterEvents(formatter: NumberFormatter) {
        if (!formatter) {
            return;
        }
        formatter.off(NumberFormatter.EVENT_TYPE.TEXT_DISPLAY_UPDATED, this.updateTextDisplay, this);
    }
}
